import { memo, useMemo } from 'react'
import { ChevronRight } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { LoadingText } from '../../../elements/LoadingText'
import { useMessageLoader, useModels } from '../../../../store'
import type { MentionableModel, MetadataEvent } from '../../../../types'
import { faviconUrlFor } from '../../../../utils/favicon-url'
import type { MessageGroup } from '../../../../utils/messageGrouping'
import { FaviconItem } from '../favicon-item'
import { parseJsonCached } from '../tools/tool-call-utils'
import { MessageMetadata } from './message-metadata'

type StreamingMetadata = MetadataEvent | undefined

// ---------------------------------------------------------------------------
// Favicons — the ONE derivation of a group's favicon row. The header derives
// the list once; both "show the chevron at all" and the rendered row read it,
// so the two can never disagree about favicon eligibility.
// ---------------------------------------------------------------------------

function deriveGroupFavicons(
  group: MessageGroup,
  streamingMetadata: StreamingMetadata,
  isMostRecent: boolean,
): Array<string> {
  // Streaming favicons only ever belong to the most recent message's group.
  const streamingFavicons =
    isMostRecent && streamingMetadata?.message_id === group.primaryMessage.id ? streamingMetadata?.favicons : undefined
  if (streamingFavicons?.length) {
    return dedupe(streamingFavicons)
  }

  // Otherwise mine the tool responses for them.
  const toolFavicons =
    group.submessages
      ?.filter(
        (message) =>
          message.role === 'tool' && typeof message.content === 'string' && message.content.includes('favicon'),
      )
      ?.flatMap((message) => {
        const searchResults = parseJsonCached(message, message.content as string)
        return (
          searchResults?.results
            // Get more results to have better deduplication
            ?.slice(0, 5)
            ?.map((result: any) => result.favicon || (result.url ? faviconUrlFor(result.url) : null))
            ?.filter(Boolean) || []
        )
      }) || []

  return dedupe(toolFavicons)
}

const FaviconDisplay = memo(function FaviconDisplay({ favicons }: { favicons: Array<string> }) {
  if (favicons.length === 0) {
    return null
  }

  return (
    <div className="flex items-center">
      {favicons.map((favicon: string, index: number) => (
        <FaviconItem
          key={`favicon-${favicon}-${index}`}
          src={favicon}
          zIndex={favicons.length - index}
          className={twMerge('h-5 w-5', index > 0 && '-ml-1')}
          showFallback={false}
          shadow={index < favicons.length - 1}
        />
      ))}
    </div>
  )
})

// Limit to 3 unique favicons, deduplicated by exact URL.
function dedupe(favicons: Array<string>): Array<string> {
  const unique: Array<string> = []
  for (const favicon of favicons) {
    if (!unique.includes(favicon)) unique.push(favicon)
  }
  return unique.slice(0, 3)
}

const modelShowcase = (model: MentionableModel) => (
  <span className="text-tertiary font-base text-[13px] leading-normal"> - {model.meta?.displayName}</span>
)

// ---------------------------------------------------------------------------
// Header — the group's metadata chevron row (favicons | model | loader text)
// ---------------------------------------------------------------------------

export interface MessageHeaderProps {
  group: MessageGroup
  streamingMetadata: StreamingMetadata
  isMostRecent: boolean
  isTurnActive: boolean
  isExpanded: boolean
  onToggle: () => void
}

export const MessageHeader = memo(function MessageHeader({
  group,
  streamingMetadata,
  isMostRecent,
  isTurnActive,
  isExpanded,
  onToggle,
}: MessageHeaderProps) {
  const models = useModels()
  const primaryModelId = 'model_id' in group.primaryMessage ? group.primaryMessage.model_id : undefined
  const model = useMemo(() => models.find((m) => m.value === primaryModelId), [models, primaryModelId])
  const messageLoader = useMessageLoader(group.primaryMessage.id)

  const favicons = useMemo(
    () => deriveGroupFavicons(group, streamingMetadata, isMostRecent),
    [group, streamingMetadata, isMostRecent],
  )
  const hasFavicons = favicons.length > 0

  const hasNamedSearch = useMemo(
    () =>
      group.submessages?.some(
        (message) =>
          message.role === 'tool' &&
          typeof message.content === 'string' &&
          (message.name === 'search_audio_notes' || message.name === 'search_knowledge'),
      ) ?? false,
    [group.submessages],
  )

  const showsModel = !!model && !hasNamedSearch
  // Nothing to label means no bare-chevron button.
  const hasContent = hasFavicons || !!messageLoader || (isTurnActive && isMostRecent) || showsModel
  if (!hasContent) return null

  const label = hasFavicons ? (
    <FaviconDisplay favicons={favicons} />
  ) : messageLoader ? (
    <LoadingText text={messageLoader.text} className="text-light-40 duration-500" />
  ) : isTurnActive && isMostRecent ? (
    // Only show streaming metadata when no favicons are available.
    <MessageMetadata />
  ) : (
    <>{showsModel && modelShowcase(model!)}</>
  )

  return (
    <button
      onClick={onToggle}
      className={twMerge(
        'relative mb-2 flex h-6 w-full cursor-pointer items-center gap-0.5 bg-transparent px-4 text-sm transition-colors duration-200',
        isExpanded ? 'text-foreground-secondary' : 'text-foreground-placeholder',
      )}
    >
      <div className="z-0 flex items-center gap-2">
        <span className="selection:bg-light-15 selection:text-light-80 text-foreground-placeholder text-sm font-normal">
          {label}
        </span>
      </div>
      <ChevronRight className={twMerge('transition-transform duration-200', isExpanded && 'rotate-90')} size={16} />
    </button>
  )
})
